import { FormatOptions, FormulaInput, LogicExpressionInput, TableCellData } from '@doss/common-all'
import { RequireExactlyOne } from 'type-fest'
import {
  DataType,
  EnumOptionInput,
  FormatPreset,
  GetOrganizationRolesWithTablePermissionsQuery,
  GetTableColumnRelatedActionStepsQuery,
  GetTableColumnsTabsChildTablesQuery,
  IndexType,
} from '~/common/generated/apollo-operations'

export const DEFAULT_COLUMN_VALUES: ColumnFormValues = {
  id: undefined,
  tempId: '',
  tableId: undefined,
  order: 1,
  name: '',
  description: null,
  type: DataType.STRING,
  innerType: null,
  isNullable: true,
  isReadOnly: false,
  isDecimal: false,
  formatPreset: null,
  formatOptions: null,
  indexType: IndexType.NONE,
  indexPrefix: '',
  indexSuffix: '',
  indexAutogenerate: false,
  displayImagesInline: false,
  enumOptions: [],
  enumOptionInputs: [],
  defaultValue: null,
  formula: null,
  formulaString: undefined,
  formulaId: null,
  lookupColumnId: null,
  lookupColumnGroupId: null,
  lookupFilterExpression: null,
  lookupFilterExpressionString: undefined,
  lookupFilterExpressionId: null,
  showLookupBacklink: false,
  lookupBacklinkName: null,
  lookupTableId: null,
  shouldCreateNewLookupGroup: false,
}

/**
 * An enum option being edited: what the write path accepts, plus `rowId` for row identity
 * independent of the label, `isSaved` for whether the label can still be changed, and
 * `iconName` for rendering.
 */
export type EnumOptionFormValue = Required<EnumOptionInput> & {
  rowId: string
  isSaved: boolean
  iconName: string | null
}

export type ColumnFormValues = {
  tableId?: string
  name: string
  description: string | null
  type: DataType
  innerType: DataType | null
  isNullable: boolean
  isReadOnly: boolean
  /** NUMBER only: workflows-as-code reads and writes the column as an exact decimal. */
  isDecimal: boolean
  formatPreset: FormatPreset | null
  formatOptions: FormatOptions | null
  indexType: IndexType
  indexPrefix: string
  indexSuffix: string
  indexAutogenerate: boolean
  displayImagesInline: boolean
  enumOptions: string[] | null
  enumOptionInputs: EnumOptionFormValue[] | null
  defaultValue?: TableCellData | null
  order: number
  formula: FormulaInput | null
  formulaString?: string | null
  formulaId?: string | null
  lookupColumnId?: string | null
  lookupColumnGroupId?: string | null
  lookupTableId?: string | null
  lookupFilterExpression?: LogicExpressionInput | null
  lookupFilterExpressionString?: string | null
  lookupFilterExpressionId?: string | null
  showLookupBacklink: boolean
  lookupBacklinkName: string | null
  shouldCreateNewLookupGroup: boolean
} & RequireExactlyOne<{ id: string; tempId: string }>

export interface FormValues {
  name: string
  columns: ColumnFormValues[]
}

export type RoleWithTablePermissions =
  GetOrganizationRolesWithTablePermissionsQuery['roles'][number]
export type TablePermissionsFormData = RoleWithTablePermissions['tablePermissions'][number][]
export type TablePermission = TablePermissionsFormData[number]

export type SimpleTable = GetTableColumnsTabsChildTablesQuery['table']
export type SimpleTableColumn = SimpleTable['columns'][number]
export type SimpleTableTab = SimpleTable['tableTabs'][number]
export type SimpleTableChildTable = SimpleTable['childTables'][number]
export type RelatedActionSteps = GetTableColumnRelatedActionStepsQuery['relatedActionSteps']
export type RelatedActionStep =
  | RelatedActionSteps['readSteps'][number]
  | RelatedActionSteps['writeSteps'][number]
